<template>
  <div class="loan-details-section-wrapper" v-show="!loading">
    <div class="loan-details-section" ref="schoolConfirmationRef" id="schoolConfirmation">
      <div class="loan-details-section-header">
        School Confirmation
        <a
          class="edit-loan-config-link"
          href="#/"
          @click="openSchoolConfirmationModal"
          :disabled="loading"
          >Edit</a
        >
      </div>
      <div class="loan-details-section-content two-column-grid">
        <div class="info-section" id="school">
          <p class="info-title">School</p>
          <div class="info">
            <p>
              {{ schoolName }}
            </p>
          </div>
        </div>
        <div class="info-section" id="graduationDate">
          <p class="info-title">Graduation Date <span class="error">*</span></p>
          <div class="info">
            {{ graduationDate }}
          </div>
        </div>
        <div class="info-section" id="academicTerms">
          <p class="info-title">Academic Term(s)</p>
          <div class="info">
            {{ academicTerms }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="loan-details-section"
    ref="loanTermsRef"
    id="interestRateAndLoanTerms"
    v-show="!loading"
  >
    <div class="loan-details-section-header">
      Interest Rate & Loan Terms
      <a
        class="edit-loan-config-link"
        href="#/"
        @click="openLoanConfigurationModal"
        :disabled="loading"
        >Edit</a
      >
    </div>
    <div class="loan-details-section-content two-column-grid">
      <div class="info-section" id="loanAmount">
        <p class="info-title">Loan Amount</p>
        <div class="info">
          <p>
            {{ loanAmount }}
          </p>
        </div>
      </div>
      <div class="info-section" id="loanAmountPerTerm">
        <p class="info-title">Amount Per Academic Term</p>
        <div class="info">
          {{ loanAmountPerTerm }}
        </div>
      </div>
      <div class="info-section" id="repaymentType">
        <p class="info-title">Repayment Type</p>
        <div class="info">
          {{ pelDetails?.repaymentTypeDisplay }}
        </div>
      </div>
      <div class="info-section" id="rateSelection">
        <p class="info-title">Rate Selection</p>
        <div class="info">
          {{ pelDetails?.rateSelectionDisplay }}
        </div>
      </div>
      <div class="info-section" id="inSchoolPayments">
        <p class="info-title">{{ monthlyInSchoolPaymentLabel }}</p>
        <div class="info">
          {{ pelDetails.monthlyInSchoolPaymentsDisplay }}
        </div>
      </div>
      <div class="info-section" id="postGradPayments">
        <p class="info-title">{{ monthlyPostSchoolPaymentLabel }}</p>
        <div class="info">
          {{ pelDetails.monthlyPostGradPaymentsDisplay }}
        </div>
      </div>
    </div>
    <div class="disclaimer">
      <p>
        Payment Estimates – See Your Approval Disclosure in your Legal Packet and your Final
        Disclosure after school certification is completed for more details.
        <b
          ><i
            >The amount shown for In-School Monthly Payments is an average over the in-school
            period.</i
          ></b
        >
        Actual payment amounts will vary based on the number of days in a month, the days between
        loan disbursements and any variable interest rate.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { PelLoanConfigurationDetails } from "@/models/pel";
import useApplicationData from "@/composables/useApplicationData";

const props = defineProps({
  loading: { type: Boolean, required: true },
});

const store = useStore();

const emit = defineEmits<{
  (e: "complete", payload: boolean): void;
  (e: "close", payload: boolean): void;
  (e: "cancel", payload: boolean): void;
  (e: "openSchoolConfirmationModal", payload: boolean): void;
  (e: "openLoanConfigurationModal", payload: boolean): void;
}>();

const { isValidProfessionalGradFlow } = useApplicationData();

const pelDetails = computed<PelLoanConfigurationDetails>(
  () => store.state.loanConfigurationModule.applicationInfo,
);

const loanAmount = computed<string>(() => pelDetails.value.loanAmountDisplay);

const loanAmountPerTerm = computed<string>(() => pelDetails.value.loanAmountPerTermDisplay);

const schoolName = computed<string>(() => pelDetails.value.schoolName);

const graduationDate = computed<string>(() => pelDetails.value.graduationDateDisplay);

const academicTerms = computed<string>(() => pelDetails.value.academicTerms);

const monthlyInSchoolPaymentLabel = computed<string>(() => {
  const label = pelDetails.value?.monthlyInSchoolPaymentLabel || "In-School";

  if (isValidProfessionalGradFlow.value) {
    return `${label} Monthly Est. Payments`;
  }

  return `${label} Monthly Payments`;
});

const monthlyPostSchoolPaymentLabel = computed<string>(() => {
  const label = pelDetails.value?.monthlyPostSchoolPaymentLabel || "Post-Grad";

  if (isValidProfessionalGradFlow.value) {
    return `${label} Monthly Est. Payments`;
  }

  return `${label} Monthly Payments`;
});

// Methods
function openSchoolConfirmationModal() {
  emit("openSchoolConfirmationModal", true);
}

function openLoanConfigurationModal() {
  emit("openLoanConfigurationModal", true);
}
</script>

<style scoped>
* {
  font-family: var(--header-font);
}

p {
  margin: 0;
  padding: 0;
}

.loan-details-section-wrapper {
  display: flex;
}

.loan-details-section-wrapper .loan-details-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 23px;
  width: 100%;
}

.loan-details-section .loan-details-section-header {
  font-weight: var(--font-weight-bold);
  font-size: 1.25rem;
  margin-bottom: 7px;
}

.loan-details-section .loan-details-section-header a.edit-loan-config-link {
  color: #000;
  font-family: var(--header-font);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: 21px; /* 175% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.loan-details-section .loan-details-section-header a.edit-loan-config-link[disabled="true"] {
  color: #c3c3c3;
  cursor: not-allowed;
}

.loan-details-section .loan-details-section-content {
  width: 100%;
}

.loan-details-section .loan-details-section-content .info-section .info-title {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: 17.175px; /* 122.68% */
}
.loan-details-section .loan-details-section-content .info-section .info {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 21px; /* 150% */
}

.loan-details-section .loan-details-section-content .info-section {
  margin-bottom: 23px;
}

.two-column-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
}

:nth-child(1 of .loan-details-section) {
  margin-bottom: 0px;
}

.info-section:is([id="academicTerms"], [id="inSchoolPayments"], [id="postGradPayments"]) {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-bottom: 0px;
}

.disclaimer {
  border-radius: 6px;
  background: #f2f3f5;
  padding: 15px;
  line-height: 28px;
  margin-top: 20px;
}

.disclaimer p {
  margin-top: 0px;
  margin-bottom: 0px;
}

.error {
  color: #8b0e04;
}

@media (max-width: 576px) {
  .two-column-grid {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .loan-details-section .loan-details-section-content .info-section {
    margin-bottom: 14px;
  }
}
</style>
